import styled from "styled-components";
import videojs from "video.js";
import { Flex } from "rebass/styled-components";
import { memo } from "react";
import { useRecoilValue } from "recoil";
import { isMobile } from "react-device-detect";
import { useExperiment } from "@statsig/react-bindings";

import { ShareDialogue } from "../ShareDialogue";
import { MatchHistoryWidget } from "../MatchHistoryWidget";
import { EditPopUp } from "../EditPopUp";
import { ButtonMenu } from "../ButtonMenu";
import { IClip } from "MVVM/@types";
import { SharePopUp } from "../SharePopUp";
import { TUserRole } from "../../@types";

import { MobileAdBanner } from "../MobileAdBanner";
import { NextUpPopUp } from "../NextUpPopUp";
import { NotificationBanner } from "../NotificationBanner";
import { NotificationButton } from "../NotificationButton";
import { useWindowSize } from "MVVM/Hooks/useWindowSize";
import { PlaylistIdState } from "MVVM/State/embeddablePlaylist";
import { ShouldDisplayOverlayState } from "MVVM/State/iframe";

const Banner = memo(
  ({ clip, userRole = "creator" }: { clip: IClip; userRole?: TUserRole }) => {
    const playlistId = useRecoilValue(PlaylistIdState);
    const experiment = useExperiment("notification_button");
    const { width } = useWindowSize();
    if (isMobile) return <MobileAdBanner clip={clip} />;

    if (!!playlistId && width <= 370 && experiment.get("render_new_button"))
      return <NotificationButton clip={clip} />;

    return <NotificationBanner clip={clip} userRole={userRole} />;
  },
);

export const Overlay = memo(
  ({
    player,
    clip,
    userRole = "creator",
  }: {
    player: videojs.Player | null;
    clip: IClip;
    userRole?: TUserRole;
  }) => {
    const shouldDisplayOverlay = useRecoilValue(ShouldDisplayOverlayState);
    return (
      <StyledContainer className="overlay" $shouldShow={shouldDisplayOverlay}>
        <Banner clip={clip} userRole={userRole} />
        <LeftContainer className="left-container">
          <ButtonMenu player={player} clip={clip} />
        </LeftContainer>
        <ContentDisplay className="content-display">
          <ShareDialogue clip={clip} />
          <MatchHistoryWidget clip={clip} userRole={userRole} />
          <SharePopUp clip={clip} />
          <EditPopUp clip={clip} />
          <NextUpPopUp clip={clip} />
        </ContentDisplay>
      </StyledContainer>
    );
  },
);

const ContentDisplay = styled(Flex)`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: flex-start;
  left: 50%;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);

  @media (max-width: 540px) {
    left: calc(50% + 17px);
    transform: translate(-50%, -50%);
  }

  @media (max-width: 440px) {
    left: calc(50% + 0px);
  }

  @media (max-width: 360px) {
    gap: 2px;
  }

  @media (min-width: 541px) and (max-width: 720px) {
    left: calc(50% + 27px);
    transform: translate(-50%, -50%);
  }

  @media (min-width: 721px) and (max-width: 900px) {
    left: calc(50% + 33px);
    transform: translate(-50%, -50%);
  }

  @media (min-width: 901px) {
    left: calc(50% + 40px);
    transform: translate(-50%, -50%);
  }

  @media (min-width: 1200px) {
    left: calc(50% + 45px);
    transform: translate(-50%, -50%);
  }

  > * {
    pointer-events: auto;
  }
`;

const LeftContainer = styled(Flex)`
  align-items: center;
  flex-direction: row;
  height: 100%;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;

  @media (min-width: 650px) {
    margin-top: 0;
  }

  @media (min-width: 1200px) {
    margin-top: 0;
  }
`;

const StyledContainer = styled(Flex)<{ $shouldShow: boolean }>`
  container-type: inline-size;
  container-name: player-overlay;
  align-items: center;
  height: 100%;
  justify-content: flex-start;
  left: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  opacity: ${({ $shouldShow }) => ($shouldShow ? 1 : 0)};
  transition:
    opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;
  visibility: ${({ $shouldShow }) => ($shouldShow ? "visible" : "hidden")};
  width: 100%;
  z-index: 2;
`;
